/**
 * "The session died under us — get a new one and try again, once."
 *
 * Shared by all three transports: `bosun.ts`'s `send` (every `quoflow.*` call), `client.ts`'s
 * `request`/`postForm` (the `/api/v1` surface) and `frappe.ts`'s `request` (the `/api/resource`
 * surface, wrapped 2026-08-08 — see that file's own comment on why it was bare until the
 * whole-branch review caught it, finding 2). They throw different error classes that happen to
 * share shape (`status`, `excType`), so `isSessionExpired` reads structurally rather than with
 * `instanceof`.
 *
 * Retry is once per request and never a loop. A second failure is a real failure.
 *
 * TO VERIFY once F1/F2 land and a real session can expire: this module has NEVER been exercised
 * against a real transport, only a `vi.fn()` stub (`sessionRetry.test.ts`, pre-2026-08-08) — see
 * `bosun.test.ts`/`frappe.test.ts`/`client.test.ts` for the integration-style tests added
 * 2026-08-08 (finding 6) that mock `fetch` at the boundary instead, and the mock's
 * `auth.expire_session` switch (`mock-server/bosun/handlers/auth.ts`) that makes this runnable
 * against a live-shaped transport before F1/F2 land for real.
 *
 * TO VERIFY separately, once F5 (the CSRF ask, `docs/bosun-frappe-asks.md` `A3b`) is answered:
 * nothing in this codebase attaches `X-Frappe-CSRF-Token` to any write. `CSRFTokenError` sits in
 * `SESSION_EXC_TYPES` below on the theory that re-exchanging might help, but it will not — a
 * missing CSRF header is not a session problem, and the retry just produces an identical second
 * failure at the cost of a wasted exchange. That is bounded (retry-once) rather than actively
 * harmful, but it is not a fix. See the design spec's §5.1 addendum for the real fix (server
 * returns `csrf_token`, or scopes `ignore_csrf` to `quoflow.*`) — deliberately not built here,
 * since the shape depends on which the Frappe team picks.
 */

import { ensureFrappeSession, hasClerkTokenSource } from './frappeSession'
import { assertOk, methodUrl } from './transport'
import { apiBase } from './apiBase'
import { parseCurrentUser } from './currentUser'

/** `exc_type` values that mean "authenticate again", as opposed to "you may not do that". */
const SESSION_EXC_TYPES = new Set(['SessionExpired', 'AuthenticationError', 'CSRFTokenError'])

/**
 * One cheap, UNAMBIGUOUS check of who the server thinks we are — `auth.get_current_user` returns
 * a real user's payload when signed in, and (per Frappe's usual shape) a Guest/empty identity
 * when the session has lapsed. Deliberately a bare `fetch`, exactly like `frappeSession.ts`'s own
 * `exchange()`, and for the same reason: this function is reached FROM `isSessionExpired`, which
 * `withSessionRetry` calls before it has decided whether to retry at all, so routing this probe
 * back through `withSessionRetry`/`send`/`request` would recurse. A network failure here is
 * treated the same as Guest — if we cannot even confirm who we are, the safer assumption is that
 * the session is gone, not that the caller hit a real role gate.
 */
async function probeIsGuest(): Promise<boolean> {
  try {
    const res = await fetch(apiBase() + methodUrl('auth', 'get_current_user'), { credentials: 'include' })
    let parsed: unknown = null
    try {
      parsed = await res.json()
    } catch {
      return true
    }
    assertOk(res.status, parsed, res.statusText)
    const user = parseCurrentUser((parsed as { message?: unknown } | null)?.message)
    return user === null
  } catch {
    return true
  }
}

/**
 * Whether an error means the Frappe session is gone.
 *
 * A 401 always counts. A 403 with a session-shaped `exc_type` (`SESSION_EXC_TYPES`) always counts.
 * A 403 with NO `excType` at all (true of `client.ts`'s error class, which carries none) also
 * counts — the safe direction, since the worst case is one wasted exchange, bounded by the
 * retry-once rule.
 *
 * A bare 403 `PermissionError` is the hard case, and the ONE case this function cannot decide from
 * the error alone. It is what a genuine ROLE GATE returns (reassigning a purchaser as a
 * non-manager, say) — but per the design spec's §5.3 addendum, it is ALSO what Frappe returns for
 * an expired/invalid `sid`: an unauthenticated request resolves to the Guest user, and
 * `is_whitelisted()` then throws `PermissionError` rather than anything session-shaped. Those two
 * causes are indistinguishable on the wire until the Frappe team ships a distinct `exc_type`
 * (`docs/bosun-frappe-asks.md` `A3c`, go-live checklist `F6`).
 *
 * Earlier versions of this function resolved that ambiguity by hard-coding an EXCLUSION —
 * treating every `PermissionError` as "not expiry, don't retry" — reasoned as "re-authenticating
 * would not help". That reasoning only holds if the 403 really is a role gate; if it is actually
 * an expired session (the more likely case for a signed-in user going idle, versus a user
 * attempting an action their role never permits), the exclusion means mid-session recovery NEVER
 * FIRES — silently defeating the one property this whole design was chosen for (§3, point 1: no
 * full-page redirect, so in-progress `/rfq/:id` and `/compare/:id` edits survive). The risk was
 * never "which value is in the set" — it was that the code hard-coded a guess in the most probable
 * failure direction.
 *
 * Fixed by turning the exclusion into a PROBE: one extra `auth.get_current_user` call, bounded by
 * the same retry-once rule as everything else here (this function runs once per `withSessionRetry`
 * attempt, so the probe never compounds into a loop). Guest (or a failed probe) → treat as expiry,
 * let the retry proceed. A real user → it is a genuine role gate, the original error stands
 * unchanged.
 *
 * This subsumes an earlier, narrower concern that this function keys only on HTTP status and would
 * miss a 200-body `SessionExpired` (`assertOk` already turns a 200-with-`exc_type` body into a
 * thrown error carrying that status, so a 200-body session failure DOES reach here as whatever
 * status `assertOk` attached — today always 200 itself, since `assertOk` does not know to remap
 * it). Same underlying problem — the wire not saying "session" clearly enough — same fix once F6
 * lands: a distinct `exc_type` this function can key on directly, no probing required, no status
 * remapping required either.
 */
export async function isSessionExpired(err: unknown): Promise<boolean> {
  if (!err || typeof err !== 'object') return false
  const status = (err as { status?: unknown }).status
  if (status === 401) return true
  if (status !== 403) return false
  const excType = (err as { excType?: unknown }).excType
  if (typeof excType !== 'string') return true
  if (SESSION_EXC_TYPES.has(excType)) return true
  if (excType === 'PermissionError') return probeIsGuest()
  return false
}

/**
 * Run `attempt`; on a session failure, re-exchange once and run it again.
 *
 * `attempt` receives whether this is the retry, purely so callers can assert on it in tests and
 * so a future caller could vary behaviour. If the re-exchange itself fails, the ORIGINAL error
 * is what propagates — the caller asked for data, and "your enquiry failed to load" is more
 * useful to them than a session-plumbing error they cannot act on.
 */
export async function withSessionRetry<T>(attempt: (isRetry: boolean) => Promise<T>): Promise<T> {
  try {
    return await attempt(false)
  } catch (err) {
    if (!hasClerkTokenSource() || !(await isSessionExpired(err))) throw err
    try {
      await ensureFrappeSession()
    } catch {
      throw err
    }
    return attempt(true)
  }
}
